import { z } from "zod";

/**
 * Wire mirrors of shore's `expense-claim.dto.ts` and `expense-claim.serializer.ts`
 * (HRMS-EXP-002 section 6, HRMS-EXP-003 for the approval block).
 *
 * Mirrored from the SHIPPED shore files rather than from the story text, which
 * differs from what landed in three places worth knowing: `cancel.reason` is
 * optional on the wire and required by the service only once a claim has an
 * approver to read it, a `recall` endpoint exists that the story does not
 * mention, and the detail carries no `attachments` array because receipts are
 * read through the attachments endpoint rather than through the claim.
 *
 * `.strict()` throughout, for the reason the category schemas give: a field
 * shore stops sending should fail here loudly rather than arrive as
 * `undefined` three components deep. Money is a 2 dp decimal STRING in both
 * directions and is never parsed into a number on the way in; the totals
 * mirror does its own decimal arithmetic.
 */

/// Shore keeps these open as plain strings on the row DTO, so the mirror does
/// too and the UI maps unknown values to a neutral chip rather than crashing.
export const CLAIM_APPROVAL_STATUSES = ["pending", "approved", "rejected", "cancelled"] as const;
export type ClaimApprovalStatus = (typeof CLAIM_APPROVAL_STATUSES)[number];

export const CLAIM_PAYMENT_STATUSES = ["unpaid", "partly_paid", "paid"] as const;
export type ClaimPaymentStatus = (typeof CLAIM_PAYMENT_STATUSES)[number];

export const CLAIM_SCOPES = ["mine", "team", "workspace"] as const;
export type ClaimScope = (typeof CLAIM_SCOPES)[number];

/// 2 dp decimal string, unsigned. Shore sends money this way and takes it back
/// the same way; see the header on why it is never a number here.
const MONEY = /^\d{1,13}(\.\d{1,2})?$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const CURRENCY = /^[A-Z]{3}$/;

export const claimLineSchema = z
  .object({
    id: z.string(),
    expenseDate: z.string(),
    categoryId: z.string(),
    categoryName: z.string(),
    description: z.string().nullable(),
    amount: z.string(),
    /// Resets to `amount` on every edit (shore decision 4) and diverges only
    /// once an approver sanctions the line, which is what the detail
    /// highlights.
    sanctionedAmount: z.string(),
  })
  .strict();
export type ClaimLine = z.infer<typeof claimLineSchema>;

export const claimTaxSchema = z
  .object({
    id: z.string(),
    description: z.string(),
    ratePercent: z.string(),
    amount: z.string(),
  })
  .strict();
export type ClaimTax = z.infer<typeof claimTaxSchema>;

export const expenseClaimRowSchema = z
  .object({
    id: z.string(),
    membershipId: z.string(),
    memberName: z.string(),
    title: z.string(),
    approvalStatus: z.string(),
    paymentStatus: z.string(),
    isDraft: z.boolean(),
    currency: z.string(),
    totalClaimed: z.string(),
    totalSanctioned: z.string(),
    totalTaxes: z.string(),
    totalAdvanceAllocated: z.string(),
    grandTotal: z.string(),
    totalReimbursed: z.string(),
    lineCount: z.number().int(),
    receiptCount: z.number().int(),
    createdAt: z.string(),
    version: z.number().int(),
  })
  .strict();
export type ExpenseClaimRow = z.infer<typeof expenseClaimRowSchema>;

export const expenseClaimListSchema = z
  .object({
    rows: z.array(expenseClaimRowSchema),
    nextCursor: z.string().nullable(),
  })
  .strict();
export type ExpenseClaimList = z.infer<typeof expenseClaimListSchema>;

/**
 * The approval block, null while the claim is a draft.
 *
 * `decidedByName` is NOT here. The story prints it; shore does not send it, and
 * inventing it in the mirror would put an always-undefined name on the approval
 * card. The card resolves the decider through the shared membership names
 * lookup instead, which is the seam that already answers for people who have
 * left.
 */
export const claimApprovalSchema = z
  .object({
    approvalRequestId: z.string(),
    status: z.string(),
    resolvedVia: z.string(),
    decidedByMembershipId: z.string().optional(),
    decidedAt: z.string().optional(),
    decisionReason: z.string().optional(),
  })
  .strict();
export type ClaimApproval = z.infer<typeof claimApprovalSchema>;

export const claimAdvanceAllocationSchema = z
  .object({
    id: z.string(),
    employeeAdvanceId: z.string(),
    purpose: z.string(),
    allocatedAmount: z.string(),
  })
  .strict();
export type ClaimAdvanceAllocation = z.infer<typeof claimAdvanceAllocationSchema>;

export const expenseClaimDetailSchema = expenseClaimRowSchema
  .extend({
    /**
     * Whether the reader owns this claim, computed by shore because this app
     * cannot compute it: `/auth/me` returns no membership id and no route
     * hands one out, so `membershipId` on the row has nothing to be compared
     * against here. It gates the claimant's own actions (edit, recall,
     * cancel), the way `canDecide` gates the approvals module's.
     *
     * OPTIONAL IN THE MIRROR ONLY, and only until the two branches meet: the
     * flag ships on the shore expenses branch, and a river build that reached
     * an older shore would otherwise fail the whole detail parse over a
     * missing boolean. Absent reads as "not mine", so the worst case is a
     * read-only detail screen rather than a blank one.
     */
    isMine: z.boolean().optional(),
    lines: z.array(claimLineSchema),
    taxes: z.array(claimTaxSchema),
    /// HRMS-EXP-011. Each row carries the advance's `purpose`, so the detail
    /// can name what was spent without a second request for it.
    advanceAllocations: z.array(claimAdvanceAllocationSchema),
    /// grandTotal minus totalReimbursed, derived by shore and never stored.
    outstanding: z.string(),
    approval: claimApprovalSchema.nullable(),
  })
  .strict();
export type ExpenseClaimDetail = z.infer<typeof expenseClaimDetailSchema>;

/// Request bodies. Bounds copied from the shore DTO decorators exactly: a
/// client that accepts a 141-character title only moves the rejection from the
/// form to the network.
/// One advance a claim proposes to spend. Two fields only: everything else
/// about the draw is the server's to know.
export const allocationInputSchema = z
  .object({
    employeeAdvanceId: z.string().min(1),
    allocatedAmount: z.string().regex(MONEY, "Use an amount with at most 2 decimal places."),
  })
  .strict();
export type AllocationInput = z.infer<typeof allocationInputSchema>;

export const claimLineInputSchema = z
  .object({
    expenseDate: z.string().regex(ISO_DATE, "Use a date in YYYY-MM-DD format."),
    categoryId: z.string().min(1),
    description: z.string().max(500).optional(),
    amount: z.string().regex(MONEY, "Use an amount with at most 2 decimal places."),
  })
  .strict();
export type ClaimLineInput = z.infer<typeof claimLineInputSchema>;

export const claimTaxInputSchema = z
  .object({
    description: z.string().min(1).max(120),
    /// 0 < ratePercent <= 100, checked as a decimal string so a rate typed to
    /// four places survives the round trip the way shore stores it.
    ratePercent: z
      .string()
      .regex(/^\d{1,3}(\.\d{1,4})?$/, "Use a percent with at most 4 decimal places.")
      .refine((v) => Number(v) > 0 && Number(v) <= 100, "A tax rate is above 0 and at most 100."),
  })
  .strict();
export type ClaimTaxInput = z.infer<typeof claimTaxInputSchema>;

export const createExpenseClaimSchema = z
  .object({
    title: z.string().trim().min(1).max(140),
    currency: z.string().regex(CURRENCY),
    lines: z.array(claimLineInputSchema).min(1).max(50),
    taxes: z.array(claimTaxInputSchema).max(10).optional(),
    advanceAllocations: z.array(allocationInputSchema).max(20).optional(),
    attachmentIds: z.array(z.string()).max(10).optional(),
    /// Defaulted by shore to true. Sent explicitly both ways so "Save draft"
    /// and "Submit" read as two intentions at the call site rather than one
    /// call with an omitted field.
    submit: z.boolean().optional(),
  })
  .strict();
export type CreateExpenseClaim = z.infer<typeof createExpenseClaimSchema>;

/**
 * PATCH replaces each child set WHOLESALE (shore decision 4): an array that is
 * present replaces that collection, an absent one leaves it untouched, and
 * every line's sanctionedAmount resets to its amount. The editor therefore
 * sends the full arrays it rendered, never a row-wise patch.
 */
export const replaceExpenseClaimSchema = z
  .object({
    version: z.number().int().min(0),
    title: z.string().trim().min(1).max(140).optional(),
    lines: z.array(claimLineInputSchema).min(1).max(50).optional(),
    taxes: z.array(claimTaxInputSchema).max(10).optional(),
    advanceAllocations: z.array(allocationInputSchema).max(20).optional(),
    attachmentIds: z.array(z.string()).max(10).optional(),
  })
  .strict();
export type ReplaceExpenseClaim = z.infer<typeof replaceExpenseClaimSchema>;

export const submitExpenseClaimSchema = z.object({ version: z.number().int().min(0) }).strict();
export type SubmitExpenseClaim = z.infer<typeof submitExpenseClaimSchema>;

/// Returns a submitted claim to its author. No reason: it is going back to the
/// person who wrote it, not to a reader.
export const recallExpenseClaimSchema = z.object({ version: z.number().int().min(0) }).strict();
export type RecallExpenseClaim = z.infer<typeof recallExpenseClaimSchema>;

/**
 * `reason` is optional on the wire and the SERVICE requires it only on the
 * submitted path. Cancelling a draft nobody has seen needs no justification;
 * cancelling a claim sitting with an approver does, because that reason has a
 * reader. The dialog asks for it whenever the claim is not a draft.
 */
export const cancelExpenseClaimSchema = z
  .object({
    version: z.number().int().min(0),
    reason: z.string().trim().min(3).max(500).optional(),
  })
  .strict();
export type CancelExpenseClaim = z.infer<typeof cancelExpenseClaimSchema>;

/**
 * Per-line sanction (HRMS-EXP-003, design 3.4).
 *
 * A SUBSET is the normal case: an approver trims one line out of twelve and
 * the rest keep what they had. This is the one claim write that is not
 * replace-all, which is why every row names its `lineId` rather than relying
 * on position: a positional array would re-target itself the moment the
 * claimant recalled the claim and reordered its lines.
 */
export const sanctionLineInputSchema = z
  .object({
    lineId: z.string().min(1),
    sanctionedAmount: z.string().regex(MONEY, "Use an amount with at most 2 decimal places."),
  })
  .strict();
export type SanctionLineInput = z.infer<typeof sanctionLineInputSchema>;

export const sanctionExpenseClaimSchema = z
  .object({
    version: z.number().int().min(0),
    lines: z.array(sanctionLineInputSchema).min(1).max(50),
  })
  .strict();
export type SanctionExpenseClaim = z.infer<typeof sanctionExpenseClaimSchema>;

/**
 * Settlement (HRMS-EXP-003, T-058 and T-059).
 *
 * `amount` is what moved on THIS payment, not a running total: two partial
 * reimbursements are two calls. A running total would make the second call's
 * meaning depend on what the first one stored, and a retry would then pay
 * twice or not at all.
 *
 * The reversal requires a reason where mark-paid does not, because somebody
 * will ask why the reimbursed figure moved backwards and the answer has to be
 * in the audit row rather than in a memory.
 */
export const markClaimPaidSchema = z
  .object({
    version: z.number().int().min(0),
    amount: z.string().regex(MONEY, "Use an amount with at most 2 decimal places."),
  })
  .strict();
export type MarkClaimPaid = z.infer<typeof markClaimPaidSchema>;

export const reverseClaimPaymentSchema = z
  .object({
    version: z.number().int().min(0),
    amount: z.string().regex(MONEY, "Use an amount with at most 2 decimal places."),
    reason: z.string().trim().min(3).max(500),
  })
  .strict();
export type ReverseClaimPayment = z.infer<typeof reverseClaimPaymentSchema>;

export const listExpenseClaimsQuerySchema = z
  .object({
    scope: z.enum(CLAIM_SCOPES).optional(),
    approvalStatus: z.enum(CLAIM_APPROVAL_STATUSES).optional(),
    paymentStatus: z.enum(CLAIM_PAYMENT_STATUSES).optional(),
    membershipId: z.string().optional(),
    cursor: z.string().optional(),
    limit: z.number().int().min(1).max(100).optional(),
  })
  .strict();
export type ListExpenseClaimsQuery = z.infer<typeof listExpenseClaimsQuerySchema>;
